import React, { useState, useEffect } from 'react';
import { Camera, User, AlertCircle, X } from 'lucide-react';
import { parse, addMinutes, format, isBefore } from 'date-fns';
import "./timetable.css";
import api from '../../../service/caller';

const isTimeInSchedule = (timeSlot, startTime, endTime) => {
  if (!startTime || !endTime) return false;
  
  const [slotStart, slotEnd] = timeSlot.split(' - ');
  const scheduleStart = parse(startTime, 'HH:mm', new Date());
  const scheduleEnd = parse(endTime, 'HH:mm', new Date());
  const slotStartTime = parse(slotStart, 'HH:mm', new Date());
  const slotEndTime = parse(slotEnd, 'HH:mm', new Date());

  return slotStartTime >= scheduleStart && slotEndTime <= scheduleEnd;
};

const TimeSlot = ({ selection, onSelect, time, isAvailable = true, isPast = false, isReserved = false, appointmentType = '', isEmergency = false, isInactive = false }) => {
  const [showIcons, setShowIcons] = useState(false);

  const handleClick = () => {
    if (isInactive) {
      // Don't do anything for inactive days
      return;
    }
    
    if (!selection && isAvailable && !isPast && !isReserved) {
      setShowIcons(!showIcons);
    }
  };

  const getBackgroundColor = () => {
    if (isInactive) {
      return 'bg-gray-200 cursor-not-allowed';
    }
    if (isReserved) {
      return 'bg-red-100 cursor-not-allowed';
    }
    if (isPast) {
      return 'bg-blue-200 cursor-not-allowed';
    }
    if (selection) {
      return 'bg-pink-100';
    }
    if (!isAvailable) {
      return 'bg-gray-300 opacity-50 cursor-not-allowed';
    }
    if (isEmergency) {
      return 'bg-orange-100';
    }
    return 'bg-green-100';
  };  

  const getIcon = () => {
    if (isInactive) {
      return <X className="w-4 h-4" />;
    }
    if (isReserved) {
      return appointmentType === 'camera' || appointmentType === 'online' ? (
        <Camera className="w-4 h-4" />
      ) : (
        <User className="w-4 h-4" />
      );
    }
    if (isEmergency) {
      return <AlertCircle className="w-4 h-4 text-orange-500" />;
    }
    if (selection === 'camera') {
      return <Camera className="w-4 h-4" />;
    }
    if (selection === 'person') {
      return <User className="w-4 h-4" />;
    }
    return null;
  };

  return (
    <div
      className={`relative p-2 border border-gray-200 h-12 cursor-pointer
        ${getBackgroundColor()}
        ${!isInactive && !isReserved && !isPast && isAvailable ? 'hover:opacity-80' : ''}
        flex items-center justify-center timetablelast
      `}
      onClick={handleClick}
    >
      {getIcon()}
      {isInactive && <span className="text-xs text-gray-500">Non disponible</span>}

      {showIcons && !selection && isAvailable && !isPast && !isReserved && !isInactive && (
        <div className="absolute top-0 left-0 right-0 bottom-0 bg-white flex justify-center items-center gap-4 z-10">
          <Camera
            className="w-6 h-6 hover:text-blue-500 cursor-pointer"
            onClick={() => {
              onSelect('camera', time);
              setShowIcons(false);
            }}
          />
          <User
            className="w-6 h-6 hover:text-blue-500 cursor-pointer"
            onClick={() => {
              onSelect('person', time);
              setShowIcons(false);
            }}
          />
        </div>
      )}
    </div>
  );
};

const CustomScheduler = ({ doctorSchedule, onAppointmentSaved, doctorId }) => {
  const [selections, setSelections] = useState({});
  const [currentWeek, setCurrentWeek] = useState({
    startDate: new Date(),
    endDate: new Date(),
  });
  const [timeSlots, setTimeSlots] = useState([]);
  const [days, setDays] = useState([]);
  const [inactiveDays, setInactiveDays] = useState([]);
  const [duration_unit, setDurationunit] = useState(30); // Default duration
  const [modalData, setModalData] = useState(null);
  const [savedAppointment, setSavedAppointment] = useState(null);
  const [savedAppointments, setSavedAppointments] = useState([]);
  const [weeks, setWeeks] = useState([]); // Liste des semaines disponibles

  // Récupérer les créneaux réservés via l'API avec axios
  useEffect(() => {
    const fetchReservedSlots = async () => {
      try {
        const response = await api.get(`/clinic/reservedSlots?doctorId=${doctorId}`);
        setSavedAppointments(response.data.reservedSlots || []); // Stocke les créneaux réservés
      } catch (error) {
        console.error('Error fetching reserved slots:', error);
      }
    };

    if (doctorId) {
      fetchReservedSlots();
    }
  }, [doctorId]);

  // Process the doctor's schedule when it changes
  useEffect(() => {
    if (!doctorSchedule || doctorSchedule.length === 0) return;

    // Extract both active and inactive days
    processSchedule();
  }, [doctorSchedule]);

  const processSchedule = () => {
    // Create a list of all days of the week
    const allDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    
    // Filter active days (those with available times)
    const activeDays = [];
    const unavailableDays = [];
    const allTimeSlots = new Set();
    
    // Process each schedule item to identify active and inactive days
    doctorSchedule.forEach(item => {
      if (item.date && item.status === 'unavailable') {
        // This is an inactive day entry with just a date marker
        const dayName = new Date(item.date).toLocaleString('en-US', { weekday: 'long' });
        if (!unavailableDays.includes(dayName)) {
          unavailableDays.push(dayName);
        }
      } else if (item.date && item.time && item.status === 'available') {
        // This is an active day with available time slots
        const dayName = new Date(item.date).toLocaleString('en-US', { weekday: 'long' });
        if (!activeDays.includes(dayName)) {
          activeDays.push(dayName);
        }
        
        // Add the time slot
        const endTime = calculateEndTime(item.time, item.durationApt || 30);
        allTimeSlots.add(`${item.time} - ${endTime}`);
      }
    });
    
    // Find days that are in allDays but not in activeDays (these are truly inactive)
    const inactiveDaysList = allDays.filter(day => 
      !activeDays.includes(day) || unavailableDays.includes(day)
    );
    
    // Update state
    setDays(allDays);
    setInactiveDays(inactiveDaysList);
    setTimeSlots(Array.from(allTimeSlots).sort());
    
    // Set up weeks for date navigation
    setupWeeks();
  };

  const setupWeeks = () => {
    // Set up a 4-week period from today
    const startDate = new Date();
    const availableWeeks = [];
    
    // Generate 4 weeks
    for (let i = 0; i < 4; i++) {
      const weekStartDate = new Date(startDate);
      weekStartDate.setDate(startDate.getDate() + (i * 7));
      
      const weekEndDate = new Date(weekStartDate);
      weekEndDate.setDate(weekStartDate.getDate() + 6);
      
      availableWeeks.push({ 
        startDate: weekStartDate, 
        endDate: weekEndDate 
      });
    }
    
    setWeeks(availableWeeks);
    if (availableWeeks.length > 0) {
      setCurrentWeek(availableWeeks[0]);
    }
  };

  const calculateEndTime = (startTime, durationMinutes) => {
    const [hours, minutes] = startTime.split(':').map(part => parseInt(part, 10));
    
    let totalMinutes = hours * 60 + minutes + durationMinutes;
    const newHours = Math.floor(totalMinutes / 60);
    const newMinutes = totalMinutes % 60;
    
    return `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
  };

  const isEmergencySlot = (day, time) => {
    if (!doctorSchedule) return false;
    
    return doctorSchedule.some(item => {
      if (!item.date || !item.time) return false;
      
      const itemDay = new Date(item.date).toLocaleString('en-US', { weekday: 'long' });
      return itemDay === day && 
             item.time === time.split(' - ')[0] && 
             item.type === 'emergency';
    });
  };

  // Vérification si le créneau est réservé en tenant compte uniquement des heures
  const isSlotReserved = (day, timeSlot) => {
    // Extraire l'heure de début du créneau
    const slotStartTime = timeSlot.split(' - ')[0];
    
    return savedAppointments.some(appointment => {
      // Vérifier si l'appointment est le même jour
      const date = new Date(appointment.appointmentDate);
      const dayOfWeek = date.toLocaleString('en-US', { weekday: 'long' });
      
      if (dayOfWeek !== day) {
        return false;
      }
      
      // Comparer uniquement les heures de début
      return appointment.appointmentTime === slotStartTime;
    });
  };

  const handleSelection = (type, time, day) => {
    const slotKey = `${day}-${time}`;
    setSelections((prev) => ({
      ...prev,
      [slotKey]: type,
    }));
    setModalData({ type, time, day });
  };

  const handleCloseModal = () => {
    if (modalData) {
      setSelections((prev) => {
        const newSelections = { ...prev };
        const slotKey = `${modalData.day}-${modalData.time}`;
        delete newSelections[slotKey];
        return newSelections;
      });
    }
    setModalData(null);
  };

  const changeWeek = (direction) => {
    setCurrentWeek((prev) => {
      const currentIndex = weeks.findIndex(
        (week) => week.startDate.getTime() === prev.startDate.getTime()
      );

      let newIndex = currentIndex + (direction === "prev" ? -1 : 1);
      if (newIndex < 0) newIndex = 0;
      if (newIndex >= weeks.length) newIndex = weeks.length - 1;

      return weeks[newIndex];
    });
  };

  const formatDateRange = () => {
    if (!currentWeek || !currentWeek.startDate || !currentWeek.endDate) return "";
    
    const { startDate, endDate } = currentWeek;
    return `${startDate.getDate()} ${startDate.toLocaleString("default", { month: "long" })} ${startDate.getFullYear()} - ${endDate.getDate()} ${endDate.toLocaleString("default", { month: "long" })}`;
  };

  const handleSaveAppointment = () => {
    if (!modalData) return;

    const { startDate } = currentWeek;

    // Créer une nouvelle instance de Date pour la date complète
    const appointmentDate = new Date(startDate);

    // Ajuster la date pour le jour sélectionné
    const dayOffsets = {
      'Monday': 0,
      'Tuesday': 1,
      'Wednesday': 2,
      'Thursday': 3,
      'Friday': 4,
      'Saturday': 5,
      'Sunday': 6
    };
    
    const dayOffset = dayOffsets[modalData.day];
    
    if (dayOffset !== undefined) {
      appointmentDate.setDate(startDate.getDate() + dayOffset);
    } else {
      console.error(`Invalid day: ${modalData.day}`);
      return;
    }

    // Extraire l'heure de début du créneau sélectionné
    const timeStart = modalData.time.split(' - ')[0];
    const appointmentTime = timeStart;

    // Vérification si le rendez-vous est dans le futur
    const [hours, minutes] = timeStart.split(':').map(num => parseInt(num, 10));
    const appointmentDateTime = new Date(appointmentDate);
    appointmentDateTime.setHours(hours, minutes, 0, 0);
    
    if (appointmentDateTime <= new Date()) {
      alert("La date sélectionnée est déjà passée. Veuillez choisir une autre date.");
      return;
    }

    // Find the appropriate duration for this appointment
    let duration = 30; // Default
    const matchingSlot = doctorSchedule.find(item => {
      if (!item.date || !item.time) return false;
      
      const itemDay = new Date(item.date).toLocaleString('en-US', { weekday: 'long' });
      return itemDay === modalData.day && item.time === timeStart;
    });
    
    if (matchingSlot && matchingSlot.durationApt) {
      duration = matchingSlot.durationApt;
    }

    // Crée un objet avec toutes les données sélectionnées
    const appointmentschedule = {
      date: appointmentDate,
      time: appointmentTime,
      type: modalData.type,
      durationApt: duration
    };

    // Appel à une fonction passée en props pour envoyer les données au parent
    if (onAppointmentSaved) {
      onAppointmentSaved(appointmentschedule);
    }

    // Sauvegarder l'état du rendez-vous pour affichage (confirmation)
    setSavedAppointment(appointmentschedule);
    setModalData(null); // Réinitialiser les données du modal
  };

  // If there's no doctorSchedule, show a message
  if (!doctorSchedule || doctorSchedule.length === 0) {
    return <div className="p-4 text-center">No schedule available</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-4 timetablelast">
      {/* Week selector */}
      <div className="flex justify-between items-center mb-4">
        <button 
          className="p-2 bg-gray-200 rounded" 
          onClick={() => changeWeek("prev")}
          disabled={weeks.length === 0 || weeks.indexOf(currentWeek) === 0}
        >
          &lt;
        </button>
        <h2 className="text-lg font-semibold">{formatDateRange()}</h2>
        <button 
          className="p-2 bg-gray-200 rounded" 
          onClick={() => changeWeek("next")}
          disabled={weeks.length === 0 || weeks.indexOf(currentWeek) === weeks.length - 1}
        >
          &gt;
        </button>
      </div>
      
      {/* Legend */}
      <div className="flex flex-wrap gap-4 mb-4 justify-center">
        <div className="flex items-center">
          <div className="w-4 h-4 bg-green-100 mr-2"></div>
          <span className="text-sm">Disponible</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 bg-red-100 mr-2"></div>
          <span className="text-sm">Réservé</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 bg-blue-200 mr-2"></div>
          <span className="text-sm">Passé</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 bg-orange-100 mr-2"></div>
          <span className="text-sm">Urgence</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 bg-gray-200 mr-2"></div>
          <span className="text-sm">Non disponible</span>
        </div>
      </div>

      <div className="grid grid-cols-[auto_repeat(7,1fr)] gap-1">
        <div className="font-semibold p-2">Time</div>
        {days.map((day) => (
          <div key={day} className={`font-semibold p-2 text-center ${inactiveDays.includes(day) ? 'text-gray-400' : ''}`}>
            {day}
          </div>
        ))}

        {timeSlots.length > 0 ? (
          timeSlots.map((time) => (
            <React.Fragment key={time}>
              <div className="p-2 text-sm">{time}</div>
              {days.map((day) => {
                const isInactive = inactiveDays.includes(day);
                const isReserved = isSlotReserved(day, time);
                const appointment = savedAppointments.find(
                  (apt) => apt.appointmentTime === time.split(' - ')[0] && 
                  new Date(apt.appointmentDate).toLocaleString('en-US', { weekday: 'long' }) === day
                );
                const appointmentType = appointment ? appointment.type_consultation : '';
                
                // Find if this time slot is available in the schedule
                const isAvailable = !isInactive && doctorSchedule.some(item => {
                  if (!item.date || !item.time) return false;
                  
                  const itemDay = new Date(item.date).toLocaleString('en-US', { weekday: 'long' });
                  return itemDay === day && 
                         item.time === time.split(' - ')[0] && 
                         item.status === 'available';
                });

                // Calculate if the time slot is in the past
                const currentDate = new Date();
                const timeSlotDate = new Date(currentWeek.startDate);
                const [hour, minute] = time.split(' - ')[0].split(':').map((t) => parseInt(t, 10));
                
                const dayOffsets = {
                  'Monday': 0,
                  'Tuesday': 1,
                  'Wednesday': 2,
                  'Thursday': 3,
                  'Friday': 4,
                  'Saturday': 5,
                  'Sunday': 6
                };
                
                const dayOffset = dayOffsets[day] || 0;
                timeSlotDate.setDate(timeSlotDate.getDate() + dayOffset);
                timeSlotDate.setHours(hour, minute);

                const isPast = timeSlotDate < currentDate;
                const isEmergency = isEmergencySlot(day, time);

                return (
                  <TimeSlot
                    key={`${day}-${time}`}
                    time={time}
                    isAvailable={isAvailable}
                    selection={selections[`${day}-${time}`]}
                    isPast={isPast}
                    onSelect={(type) => handleSelection(type, time, day)}
                    isReserved={isReserved}
                    appointmentType={appointmentType}
                    isEmergency={isEmergency}
                    isInactive={isInactive}
                  />
                );
              })}
            </React.Fragment>
          ))
        ) : (
          <div className="col-span-8 p-4 text-center">No time slots available</div>
        )}
      </div>

      {/* Modal */}
      {modalData && (
        <div className="modal-overlay">
          <div className="modal-content1">
            <p className="modal-text">
              Create appointment for <strong>{modalData.day}</strong>, <strong>{modalData.time}</strong>?
            </p>
            <div className="modal-buttons">
              <button
                className="modal-button close"
                onClick={handleCloseModal}
              >
                Close
              </button>
              <button
                className="modal-button confirm"
                onClick={handleSaveAppointment}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomScheduler;